// Drain / run-off monitoring — the pure capture-side logic behind
// /farm/drain-monitoring and /farm/drain-report (src/modules/farm/Drain*.tsx):
// % run-off derivation, draft building, validation, station grouping and the
// shared CSV row. Extracted so the maths and rules are unit-testable
// (drainReadings.test.ts) and the pages stay presentational.

import { isManagerOrAbove } from '@/lib/roles'
import { compareNatural } from '@/lib/naturalSort'
import type { UserRole } from '@/types/database'
import type { DrainReading, DrainTargets } from '@/lib/advisors/drainRunoff'

// ─────────────────────────────────────────────────────────────── row shapes
export interface StationRow {
  station_id: string; block_id: string; lane_label: string; variety: string | null
  bag_count: number; sort_order: number; version: number
}
export interface ReadingRow {
  measurement_id: string; station_id: string; measured_date: string
  in_ph: number | null; in_ec: number | null; in_volume_ml: number | null; in_temp_c: number | null
  out_ph: number | null; out_ec: number | null; out_volume_ml: number | null; out_temp_c: number | null
  no_drain: boolean; notes: string | null; runoff_pct: number | null; version: number
}
export interface BlockOption { block_id: string; block_name: string; commodity: string | null }

// ──────────────────────────────────────────────────────────── page plumbing

/** Role gates for the drain pages — must mirror the drain_stations /
 *  drain_measurements RLS: station setup and removing a reading = manager+;
 *  capture and correcting a reading = + foreman + scout (FW1 UF-12, mig
 *  20261231000000). `canPlan` is also what shows "Remove reading". */
export function drainPageRoles(me: { role?: UserRole | null } | null | undefined): { canPlan: boolean; canLog: boolean } {
  const role = me?.role ?? null
  const canPlan = isManagerOrAbove(role)
  return { canPlan, canLog: canPlan || role === 'FARM_FOREMAN' || role === 'FARM_SCOUT' }
}

/** blocksQ post-processing (the WaterSchedule idiom): keep only blocks whose
 *  grower is in current_farm_owned_grower_ids(), flattened for pickers. */
export function ownVisibleBlocks(
  visibleIds: string[] | null,
  rows: Array<{ block_id: string; block_name: string; commodity?: string | null; pucs?: { grower_id: string | null } | null }> | null,
): BlockOption[] {
  const visible = new Set<string>(visibleIds ?? [])
  return (rows ?? [])
    .filter(r => visible.has(r.pucs?.grower_id ?? ''))
    .map(r => ({ block_id: r.block_id, block_name: r.block_name, commodity: r.commodity ?? null }))
}

/** Index rows by key — station_id → reading, block_id → block, … */
export function indexByKey<T>(rows: T[] | null | undefined, keyOf: (r: T) => string): Record<string, T> {
  const m: Record<string, T> = {}
  for (const r of rows ?? []) m[keyOf(r)] = r
  return m
}

export interface StationGroup {
  blockId: string
  /** Raw block name for ordering ('' when the block is not visible). */
  sortName: string
  blockName: string
  commodity: string | null
  stations: StationRow[]
}

/** FM05 UF-10: walking order — a farm's own sort_order wins where it set one,
 *  then the lane label naturally (Lane 2 before Lane 10). */
export function byWalkingOrder(a: Pick<StationRow, 'sort_order' | 'lane_label'>, b: Pick<StationRow, 'sort_order' | 'lane_label'>): number {
  return (a.sort_order ?? 0) - (b.sort_order ?? 0) || compareNatural(a.lane_label, b.lane_label)
}

/** FM05 UF-09: the station to open after a save — the next one in walking
 *  order (the groups' order, block by block) with no reading today and none
 *  waiting on this phone, wrapping round past the end; null when all are done. */
export function nextStationToDo(
  groups: ReadonlyArray<{ stations: StationRow[] }>, current: string,
  done: (stationId: string) => boolean,
): StationRow | null {
  const order = groups.flatMap(g => g.stations)
  const at = order.findIndex(s => s.station_id === current)
  for (let i = 1; i <= order.length; i++) {
    const s = order[(at + i) % order.length]
    if (s.station_id !== current && !done(s.station_id)) return s
  }
  return null
}

/** Group stations under their block, ordered by block name (a station whose
 *  block is not visible groups under 'Unknown block', sorting first). */
export function groupStationsByBlock(
  stations: StationRow[],
  blocksById: Record<string, BlockOption>,
): StationGroup[] {
  const byBlock = new Map<string, StationRow[]>()
  for (const s of stations) {
    const list = byBlock.get(s.block_id) ?? []
    list.push(s)
    byBlock.set(s.block_id, list)
  }
  return Array.from(byBlock.entries())
    .map(([blockId, list]) => {
      const b = blocksById[blockId]
      return {
        blockId,
        sortName: b != null ? b.block_name : '',
        blockName: b != null ? b.block_name : 'Unknown block',
        commodity: b != null ? b.commodity : null,
        stations: [...list].sort(byWalkingOrder),
      }
    })
    // FM05 UF-10: Block 2 before Block 10 — names an operator scans sort
    // naturally. 'Unknown block' (no name) still sorts FIRST, as before.
    .sort((a, b) => (a.sortName === '' ? -1 : 0) - (b.sortName === '' ? -1 : 0) || compareNatural(a.sortName, b.sortName))
}

/** The station's IN-only preview line ("pH 6.43 → 4.73 · EC 1.06 → 1.56 ·
 *  4600 ml in") — null when there is nothing to summarise (no reading, or
 *  flagged no-drain: the state chip carries that story). */
export function readingSummary(r: ReadingRow | null): string | null {
  if (!r || r.no_drain) return null
  const parts: string[] = []
  if (r.in_ph != null) parts.push(`pH ${fmtMeasure(r.in_ph)}${r.out_ph != null ? ` → ${fmtMeasure(r.out_ph)}` : ''}`)
  if (r.in_ec != null) parts.push(` · EC ${fmtMeasure(r.in_ec)}${r.out_ec != null ? ` → ${fmtMeasure(r.out_ec)}` : ''}`)
  if (r.in_volume_ml != null) parts.push(` · ${r.in_volume_ml} ml in`)
  return parts.join('')
}

/** Trailing-zero-trimmed measurement ("6.40" → "6.4", "5.00" → "5"). */
export function fmtMeasure(n: number): string {
  return Number(n).toFixed(2).replace(/\.?0+$/, '')
}

export interface DrainStateChip { label: string; className: string }

/** The station row's right-hand state chip: not captured / no water /
 *  IN only / the run-off % coloured by the target band. `known=false` (the
 *  day's readings query holds no data — errored or paused) renders a distinct
 *  "unknown" chip: a failed read must never claim a station was skipped. */
export function drainStateChip(reading: ReadingRow | null, targets: DrainTargets, known = true): DrainStateChip {
  if (!known) return { label: 'unknown', className: 'text-[11px] px-2 py-1 rounded-full bg-gray-800 text-gray-500 border border-dashed border-gray-600' }
  if (!reading) return { label: 'not captured', className: 'text-[11px] px-2 py-1 rounded-full bg-gray-700/60 text-gray-400' }
  if (reading.no_drain) return { label: 'no water', className: 'text-[11px] px-2 py-1 rounded-full bg-red-900/50 text-red-300' }
  if (reading.runoff_pct == null) return { label: 'IN only', className: 'text-[11px] px-2 py-1 rounded-full bg-sky-900/50 text-sky-300' }
  const inBand = reading.runoff_pct >= targets.runoffMinPct && reading.runoff_pct <= targets.runoffMaxPct
  return {
    label: `${Number(reading.runoff_pct).toFixed(1)}%`,
    className: `text-[12px] px-2 py-1 rounded-full font-semibold tabular-nums ${inBand ? 'bg-emerald-900/50 text-emerald-300' : 'bg-amber-900/50 text-amber-300'}`,
  }
}

// ─────────────────────────────────────────────────────────── capture maths

export interface DrainFormValues {
  inPh: string; inEc: string; inVol: string; inTemp: string
  outPh: string; outEc: string; outVol: string; outTemp: string
  noDrain: boolean; notes: string
}

const str = (v: number | null): string => (v == null ? '' : String(v))

/** Seed the capture form from the existing reading (edit) or blank (new). */
export function initialDrainForm(existing: ReadingRow | null): DrainFormValues {
  if (!existing) {
    return { inPh: '', inEc: '', inVol: '', inTemp: '', outPh: '', outEc: '', outVol: '', outTemp: '', noDrain: false, notes: '' }
  }
  return {
    inPh: str(existing.in_ph), inEc: str(existing.in_ec), inVol: str(existing.in_volume_ml), inTemp: str(existing.in_temp_c),
    outPh: str(existing.out_ph), outEc: str(existing.out_ec), outVol: str(existing.out_volume_ml), outTemp: str(existing.out_temp_c),
    noDrain: existing.no_drain, notes: existing.notes ?? '',
  }
}

export const numOrNull = (s: string): number | null => (s.trim() === '' ? null : Number(s))

export type DrainDraft = DrainReading & { in_temp_c: number | null; out_temp_c: number | null }

export interface DrainEntry {
  draft: DrainDraft
  /** Live % run-off preview (OUT/IN×100); 0 when flagged no-drain; null before both volumes exist. */
  runoffPreview: number | null
  /** Set when the preview exceeds 100% — possible after rain, otherwise a mis-read. */
  overWarn: string | null
}

/** Derive the draft reading + run-off preview from the form strings. The
 *  no-drain flag blanks the OUT side; runoff_pct is rounded to 2 decimals. */
export function deriveDrainEntry(v: DrainFormValues): DrainEntry {
  const inVolN = v.inVol === '' ? null : Number(v.inVol)
  const outVolN = v.outVol === '' ? null : Number(v.outVol)
  const runoffPreview = !v.noDrain && inVolN && outVolN != null ? (outVolN * 100) / inVolN : v.noDrain ? 0 : null
  const overWarn = runoffPreview != null && runoffPreview > 100
    ? 'OUT volume is larger than IN — possible after rain, otherwise re-check the reading.' : null
  const draft: DrainDraft = {
    in_ph: numOrNull(v.inPh), in_ec: numOrNull(v.inEc), in_volume_ml: inVolN, in_temp_c: numOrNull(v.inTemp),
    out_ph: v.noDrain ? null : numOrNull(v.outPh), out_ec: v.noDrain ? null : numOrNull(v.outEc),
    out_volume_ml: v.noDrain ? null : outVolN, out_temp_c: v.noDrain ? null : numOrNull(v.outTemp),
    no_drain: v.noDrain,
    runoff_pct: runoffPreview != null ? Math.round(runoffPreview * 100) / 100 : null,
  }
  return { draft, runoffPreview, overWarn }
}

/** First validation failure, or null when the draft may be saved. */
export function validateDrainReading(draft: DrainDraft): string | null {
  if (!draft.in_volume_ml || draft.in_volume_ml <= 0) return 'Enter the IN volume (ml) — the anchor of the % run-off.'
  for (const [label, v] of [['IN pH', draft.in_ph], ['OUT pH', draft.out_ph]] as const) {
    if (v != null && (v < 0 || v > 14)) return `${label} must be 0–14.`
  }
  for (const [label, v] of [['IN EC', draft.in_ec], ['OUT EC', draft.out_ec]] as const) {
    if (v != null && (v < 0 || v >= 20)) return `${label} looks wrong (0–20 mS/cm).`
  }
  return null
}

/** The drain_measurements column set a save writes (runoff_pct stays DB-derived). */
export function drainMeasurementFields(draft: DrainDraft, notes: string): Record<string, unknown> {
  return {
    in_ph: draft.in_ph, in_ec: draft.in_ec, in_volume_ml: draft.in_volume_ml, in_temp_c: draft.in_temp_c,
    out_ph: draft.out_ph, out_ec: draft.out_ec, out_volume_ml: draft.out_volume_ml, out_temp_c: draft.out_temp_c,
    no_drain: draft.no_drain, notes: notes.trim() || null,
  }
}

// ───────────────────────────────────────────────────────────── save helpers

export const userIdOf = (me: { id: string } | null | undefined): string | null => me?.id ?? null

/** The active tenant id a queued write is stamped with — throws the
 *  refresh-the-page message when the tenant is not resolved yet. */
export function requiredTenantId(activeTenant: { tenantId?: string | null } | null | undefined): string {
  if (!activeTenant?.tenantId) throw new Error('Active tenant missing — refresh the page.')
  return activeTenant.tenantId
}

// ─────────────────────────────────────────────────────────── station editor

/** The columns a station edit may change. The block is NOT one of them: a
 *  tray that moved to another block is a different station (remove + add),
 *  so its history stays under the block it was actually measured on. */
export interface StationFields { lane_label: string; variety: string | null; bag_count: number }
export interface StationInsert extends StationFields { block_id: string }

/** Station-editor typed fields → the column patch, or the validation
 *  message. Shared by add (with a block) and edit (same station, new labels). */
export function parseStationFields(lane: string, variety: string, bags: string): string | StationFields {
  if (!lane.trim()) return 'Give the station a lane label (e.g. "Lane A").'
  const bagCount = Number(bags)
  if (!bagCount || bagCount < 1 || bagCount > 100) return 'Bag count must be 1–100.'
  return { lane_label: lane.trim(), variety: variety.trim() || null, bag_count: bagCount }
}

/** Station-editor form → insert payload, or the validation message. */
export function parseStationForm(blockId: string, lane: string, variety: string, bags: string): string | StationInsert {
  if (!blockId) return 'Pick a block.'
  const fields = parseStationFields(lane, variety, bags)
  return typeof fields === 'string' ? fields : { block_id: blockId, ...fields }
}

export interface StationFormSeed { lane: string; variety: string; bags: string }

/** What the editor's three fields hold when it opens on a station (edit) or
 *  on nothing (add). The dirty guard compares against this, so switching a
 *  row into edit mode is not itself "typed work". */
export function stationFormSeed(station: Pick<StationRow, 'lane_label' | 'variety' | 'bag_count'> | null): StationFormSeed {
  return station
    ? { lane: station.lane_label, variety: station.variety ?? '', bags: String(station.bag_count) }
    : { lane: '', variety: '', bags: '1' }
}

// ──────────────────────────────────────────────────────────────── CSV export

export const DRAIN_CSV_HEADERS = [
  'Block', 'Lane', 'Variety', 'Date', 'In pH', 'In EC', 'In vol (ml)', 'In °C',
  'Out pH', 'Out EC', 'Out vol (ml)', 'Out °C', 'Run-off %', 'No drain', 'Notes',
] as const

/** The printed record's CSV: the shared columns plus who captured each
 *  reading (FM05 F3) — last, so no existing column moves. */
export const DRAIN_REPORT_CSV_HEADERS = [...DRAIN_CSV_HEADERS, 'Captured by'] as const

const NO_READING = {
  in_ph: null, in_ec: null, in_volume_ml: null, in_temp_c: null,
  out_ph: null, out_ec: null, out_volume_ml: null, out_temp_c: null,
  no_drain: false, notes: null, runoff_pct: null,
} as const

/** One CSV row under DRAIN_CSV_HEADERS — a station not yet captured exports
 *  blanks, a reading whose station was deleted exports a blank lane. */
export function drainCsvRow(
  blockName: string,
  station: { lane_label: string; variety: string | null } | null | undefined,
  date: string,
  r: ReadingRow | null,
): (string | number | null)[] {
  const s = station ?? { lane_label: '', variety: null }
  const m = r ?? NO_READING
  return [
    blockName, s.lane_label, s.variety ?? '', date,
    m.in_ph, m.in_ec, m.in_volume_ml, m.in_temp_c,
    m.out_ph, m.out_ec, m.out_volume_ml, m.out_temp_c,
    m.runoff_pct, m.no_drain ? 'yes' : '', m.notes ?? '',
  ]
}
